import React from 'react';

import { DataGrid } from '@gogame/data-grid';

import { Link, Typography, Grid } from '@gogame/components';
import { useChildComponent } from '@gogame/hooks-ts';
import { useCurrencyFormat } from '@gogame/intl-ts';
import { useScreenDispatch, useScreenState } from '@gogame/screens';
import InfluencerChannelsActions from 'scenes/Influencers/InfluencersChannels/components/Actions';
import Categories from 'scenes/Influencers/InfluencersChannels/components/Categories';
import {
  InfluencerChannelFormCreate,
  InfluencerChannelFormEdit,
} from 'scenes/Influencers/InfluencersChannels/components/InfluencerChannelForm';
import Influencers from 'scenes/Influencers/InfluencersChannels/components/Influencers';
import InfluencerDetails from 'scenes/Influencers/InfluencersChannels/components/Influencers/components/InfluencerDetails/InfluencerDetails';
import {
  COLUMN_ID,
  COLUMN_NAME,
  COLUMN_AGENCIES,
  COLUMN_INFLUENCER,
  COLUMN_COUNTRY,
  COLUMN_SOCIAL_MEDIA_PLATFORM,
  COLUMN_CATEGORY,
  COLUMN_SUB_CATEGORY,
  COLUMN_MASTER_CAMPAIGNS,
  COLUMN_TA,
  COLUMN_VIEWS_MIN,
  COLUMN_VIEWS_MAX,
  COLUMN_CPM_MIN,
  COLUMN_CPM_MAX,
  COLUMN_ENGAGEMENT,
  COLUMN_CLICKS_MIN,
  COLUMN_CLICKS_MAX,
  COLUMN_CPI_MIN,
  COLUMN_CPI_MAX,
  COLUMN_CONVERSIONS_MIN,
  COLUMN_CONVERSIONS_MAX,
  COLUMN_CONCURRENT_VIEWERS,
  COLUMN_PRICES,
  COLUMN_SUBSCRIBERS,
  COLUMN_LAST_UPDATED_BY,
  COLUMN_LAST_UPDATED_DATE,
  COLUMN_CREATED_BY,
  COLUMN_CREATED_DATE,
} from 'scenes/Influencers/InfluencersChannels/constants';
import { useLoadInfluencerChannelTable } from 'scenes/Influencers/InfluencersChannels/data';
import {
  SET_TABLE_SETTINGS,
  COLLAPSE_COLUMNS,
  REORDER_COLUMNS,
} from 'scenes/Influencers/InfluencersChannels/screenReducer';
import MasterCampaignDetails from 'scenes/Influencers/MasterCampaigns/components/MasterCampaignDetails';
import AffiliateDetails from 'scenes/Affiliates/components/AffiliateDetails';
import InfluencerChannelDataTableToolbar from './InfluencersChannelsDataTableToolbar';
import { InfluencersChannelsDataTableDetailsLink } from './InfluencersChannelsDataTableDetailsLink';

const InfluencerChannelDataTable = () => {
  const [childComponent, openChildComponent] = useChildComponent();
  const currencyFormat = useCurrencyFormat();

  const dispatch = useScreenDispatch();
  const screenState = useScreenState();
  const { filters, tableSettings, searchQuery, ready } = screenState;
  const params = { filters, tableSettings, searchQuery };

  const { data, isLoading, isRefetching } = useLoadInfluencerChannelTable(
    params,
    {
      enabled: ready,
    }
  );

  const handleCollapse = (collapsed) => {
    dispatch({
      type: COLLAPSE_COLUMNS,
      payload: collapsed,
    });
  };

  const handleChangeSettings = (settings) => {
    dispatch({
      type: SET_TABLE_SETTINGS,
      payload: settings,
    });
  };

  const handleReorder = (order) => {
    dispatch({
      type: REORDER_COLUMNS,
      payload: order,
    });
  };

  const handleOpenCategories = () => {
    openChildComponent(Categories);
  };

  const handleOpenInfluencers = () => {
    openChildComponent(Influencers);
  };

  const handleEdit = ({ id }) => {
    openChildComponent(InfluencerChannelFormEdit, { id });
  };

  const handleOpenAffiliate = (_e, { id }) => {
    openChildComponent(AffiliateDetails, { id });
  };

  const handleOpenInfluencer = (_e, { id }) => {
    openChildComponent(InfluencerDetails, { id });
  };

  const handleOpenMasterCampaign = (_e, { id }) => {
    openChildComponent(MasterCampaignDetails, { id });
  };

  const handleCreate = () => {
    openChildComponent(InfluencerChannelFormCreate);
  };

  return (
    <React.Fragment>
      <DataGrid
        rows={data?.rows}
        count={data?.count}
        loading={isLoading}
        fetching={isRefetching}
        onChangeSettings={handleChangeSettings}
        onReorder={handleReorder}
        onCollapse={handleCollapse}
        ready={screenState.ready}
        tableSettings={screenState.tableSettings}
        columnsState={screenState.columns}
        toolbar={
          <InfluencerChannelDataTableToolbar
            onCreate={handleCreate}
            onOpenInfluencers={handleOpenInfluencers}
            onOpenCategories={handleOpenCategories}
          />
        }
        storageKey={'influencersChannels'}
        defaultColumnWidth={120}
        filtered={!!screenState.searchQuery}
      >
        <DataGrid.Column
          field={COLUMN_ID}
          type={'text'}
          headerName={'ID'}
          align={'right'}
          headerAlign={'right'}
          width={75}
          fixed={true}
          resizable={false}
        />
        <DataGrid.Column
          field={COLUMN_NAME}
          type={'link'}
          linkComponent={InfluencersChannelsDataTableDetailsLink}
          headerName={'Name'}
          width={210}
          fixed={true}
          resizable={false}
        />
        <DataGrid.Column
          field={COLUMN_AGENCIES}
          type={'list'}
          headerName={'Agencies'}
          width={170}
          sortable={false}
          getItemValue={(v) => {
            return (
              <Link onClick={(e) => handleOpenAffiliate(e, { id: v.id })}>
                {v.name}
              </Link>
            );
          }}
        />
        <DataGrid.Column
          field={COLUMN_INFLUENCER}
          type={'link'}
          headerName={'Influencer'}
          width={170}
          onLinkClick={handleOpenInfluencer}
        />
        <DataGrid.Column
          field={COLUMN_COUNTRY}
          type={'text'}
          headerName={'Country'}
          width={170}
        />
        <DataGrid.Column
          field={COLUMN_SOCIAL_MEDIA_PLATFORM}
          type={'link'}
          headerName={'Social Media Platform'}
          width={160}
          getValue={(v) => v.name}
          linkComponent={({ children, row }) => (
            <Link href={row?.link} target={'_blank'}>
              {children}
            </Link>
          )}
        />
        <DataGrid.Column
          field={COLUMN_CATEGORY}
          type={'text'}
          headerName={'Category'}
          width={170}
        />
        <DataGrid.Column
          field={COLUMN_SUB_CATEGORY}
          type={'text'}
          headerName={'Sub Category'}
          width={170}
        />
        <DataGrid.Column
          field={COLUMN_MASTER_CAMPAIGNS}
          limit={1}
          type={'list'}
          headerName={'Master Campaigns'}
          sortable={false}
          width={132}
          getItemValue={(v) => {
            return (
              <Link onClick={(e) => handleOpenMasterCampaign(e, { id: v.id })}>
                {v.name}
              </Link>
            );
          }}
        />
        <DataGrid.Column
          field={COLUMN_TA}
          type={'percent'}
          headerName={'TA, %'}
          width={132}
        />
        <DataGrid.Column
          field={COLUMN_VIEWS_MIN}
          type={'number'}
          headerName={'Views Min.'}
          width={82}
        />
        <DataGrid.Column
          field={COLUMN_VIEWS_MAX}
          type={'number'}
          headerName={'Views Max.'}
          width={82}
        />
        <DataGrid.Column
          field={COLUMN_CPM_MIN}
          type={'number'}
          headerName={'CPM Min.'}
          width={82}
        />
        <DataGrid.Column
          field={COLUMN_CPM_MAX}
          type={'number'}
          headerName={'CPM Max.'}
          width={82}
        />
        <DataGrid.Column
          field={COLUMN_ENGAGEMENT}
          type={'number'}
          headerName={'Engagement'}
          width={96}
        />
        <DataGrid.Column
          field={COLUMN_CLICKS_MIN}
          type={'number'}
          headerName={'Clicks Min.'}
          width={82}
        />
        <DataGrid.Column
          field={COLUMN_CLICKS_MAX}
          type={'number'}
          headerName={'Clicks Max.'}
          width={82}
        />
        <DataGrid.Column
          field={COLUMN_CPI_MIN}
          type={'number'}
          headerName={'CPI Min.'}
          width={82}
        />
        <DataGrid.Column
          field={COLUMN_CPI_MAX}
          type={'number'}
          headerName={'CPI Max.'}
          width={82}
        />
        <DataGrid.Column
          field={COLUMN_CONVERSIONS_MIN}
          type={'number'}
          headerName={'Conversion Min.'}
          width={96}
        />
        <DataGrid.Column
          field={COLUMN_CONVERSIONS_MAX}
          type={'number'}
          headerName={'Conversion Max.'}
          width={96}
        />
        <DataGrid.Column
          field={COLUMN_CONCURRENT_VIEWERS}
          type={'number'}
          headerName={'Concurrent Viewers'}
          width={96}
        />
        <DataGrid.Column
          field={COLUMN_PRICES}
          type={'list'}
          headerName={'Prices'}
          sortable={false}
          align={'right'}
          headerAlign={'right'}
          getItemValue={({ price, priceWithFee }) => {
            return (
              <Grid сontainer direction={'column'} textAlign={'right'}>
                <Grid item>
                  <Typography variant={'caption'}>
                    {currencyFormat(price)}
                  </Typography>
                </Grid>
                <Grid item>
                  <Typography
                    color={'text.secondary'}
                    variant={'caption'}
                    sx={{ fontSize: 10 }}
                  >
                    with fees {currencyFormat(priceWithFee)}
                  </Typography>
                </Grid>
              </Grid>
            );
          }}
          width={96}
        />
        <DataGrid.Column
          field={COLUMN_SUBSCRIBERS}
          type={'number'}
          headerName={'Subscribers'}
          width={96}
        />
        <DataGrid.Column
          field={COLUMN_LAST_UPDATED_BY}
          type={'text'}
          headerName={'Last Updated by'}
          width={150}
        />
        <DataGrid.Column
          field={COLUMN_LAST_UPDATED_DATE}
          type={'date'}
          headerName={'Last Updated'}
          template={'LLL'}
          timeZone={'local'}
          width={150}
        />
        <DataGrid.Column
          field={COLUMN_CREATED_BY}
          type={'text'}
          headerName={'Created by'}
          width={150}
        />
        <DataGrid.Column
          field={COLUMN_CREATED_DATE}
          type={'date'}
          headerName={'Created'}
          template={'LLL'}
          timeZone={'local'}
          width={150}
        />
        <DataGrid.Column
          actionsColDef
          type={'actions'}
          actionsComponent={InfluencerChannelsActions}
          onEdit={handleEdit}
          limit={2}
        />
      </DataGrid>
      {childComponent}
    </React.Fragment>
  );
};

export default InfluencerChannelDataTable;
